<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  slideshow: Object,
  products: {
    type: Object,
    required: true
  },
})

const autoplaySpeed = 5000
const speed = 900

const currentIndex = ref(0)
const progress = ref(0)
const sliderContainer = ref(null)

let autoplayTimer = null
let progressTimer = null

const isDragging = ref(false)
const startX = ref(0)
const dragOffset = ref(0)

const totalSlides = computed(() => {
  return props.slideshow?.dynamicProperties?.length || 0
})

const currentSlideProductId = computed(() => {
  return props.slideshow?.dynamicProperties?.[currentIndex.value]?.product?.id || null
})

const commonProducts = computed(() => {
  if (!props.products?.products || !props.slideshow?.dynamicProperties) return []

  const slideshowProductIds = props.slideshow.dynamicProperties
    .map(slide => slide.product?.id)
    .filter(id => id !== undefined && id !== null)

  const filtered = props.products.products.filter(p => slideshowProductIds.includes(p.id))

  return filtered.sort((a, b) => {
    return slideshowProductIds.indexOf(a.id) - slideshowProductIds.indexOf(b.id)
  })
})

const navigateToProductSlide = (productId) => {
  if (!props.slideshow?.dynamicProperties) return

  const targetIndex = props.slideshow.dynamicProperties.findIndex(
    slide => slide.product?.id === productId
  )

  if (targetIndex !== -1) {
    goToSlide(targetIndex)
  }
}

const calculatedOffset = computed(() => {
  const containerWidth = sliderContainer.value?.offsetWidth || window.innerWidth
  const baseOffset = -currentIndex.value * containerWidth

  if (isDragging.value) {
    if (currentIndex.value === 0 && dragOffset.value > 0) {
      return baseOffset + Math.pow(dragOffset.value, 0.85)
    }
    if (currentIndex.value === totalSlides.value - 1 && dragOffset.value < 0) {
      return baseOffset - Math.pow(Math.abs(dragOffset.value), 0.85)
    }
    return baseOffset + dragOffset.value
  }
  return baseOffset
})

const startSlider = () => {
  stopSlider()
  if (totalSlides.value <= 1) return

  autoplayTimer = setInterval(() => { nextSlide() }, autoplaySpeed)

  const step = (100 / autoplaySpeed) * 100
  progressTimer = setInterval(() => {
    if (progress.value < 100) progress.value += step
  }, 100)
}

const stopSlider = () => {
  clearInterval(autoplayTimer)
  clearInterval(progressTimer)
  progress.value = 0
}

const nextSlide = () => {
  if (totalSlides.value === 0) return
  currentIndex.value = (currentIndex.value + 1) % totalSlides.value
  startSlider()
}

const prevSlide = () => {
  if (totalSlides.value === 0) return
  currentIndex.value = (currentIndex.value - 1 + totalSlides.value) % totalSlides.value
  startSlider()
}

const goToSlide = (index) => {
  currentIndex.value = index
  startSlider()
}

const handleDragStart = (e) => {
  if (totalSlides.value <= 1) return
  if (e.type === 'mousedown') e.preventDefault()

  isDragging.value = true
  startX.value = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
  dragOffset.value = 0
  stopSlider()

  if (e.type === 'mousedown') {
    window.addEventListener('mousemove', handleDragMove)
    window.addEventListener('mouseup', handleDragEnd)
  }
}

const handleDragMove = (e) => {
  if (!isDragging.value) return
  const currentX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
  dragOffset.value = currentX - startX.value
}

const handleDragEnd = () => {
  if (!isDragging.value) return
  isDragging.value = false

  window.removeEventListener('mousemove', handleDragMove)
  window.removeEventListener('mouseup', handleDragEnd)

  const containerWidth = sliderContainer.value?.offsetWidth || window.innerWidth
  const threshold = containerWidth * 0.15

  if (dragOffset.value < -threshold) {
    nextSlide()
  } else if (dragOffset.value > threshold) {
    prevSlide()
  } else {
    startSlider()
    const originalOffset = dragOffset.value
    dragOffset.value = originalOffset
    setTimeout(() => { dragOffset.value = 0 }, 10)
  }
}

watch(currentIndex, () => {
  progress.value = 0
  dragOffset.value = 0
})

onMounted(() => {
  startSlider()
})

onUnmounted(() => {
  stopSlider()
  window.removeEventListener('mousemove', handleDragMove)
  window.removeEventListener('mouseup', handleDragEnd)
})
</script>

<template>
  <div v-if="totalSlides > 0" class="rural-slider-container" ref="sliderContainer">

    <div
      class="slider-track"
      :class="{ 'is-dragging': isDragging }"
      :style="{
        transform: `translate3d(${calculatedOffset}px, 0px, 0px)`,
        transition: isDragging ? 'none' : `transform ${speed}ms ease-in-out`
      }"
      @mousedown="handleDragStart"
      @touchstart="handleDragStart($event)"
      @touchmove="handleDragMove($event)"
      @touchend="handleDragEnd">

      <div
        v-for="(slide, index) in slideshow.dynamicProperties"
        :key="index"
        class="slider-slide">
        <a target="_self">
          <template v-if="slide.featured_image?.mime_type?.includes('video')">
            <video class="video-mobile md:tw-hidden" loop muted autoplay playsinline disablepictureinpicture>
              <source :src="slide.featured_image.fullUrl" type="video/mp4">
            </video>
            <video class="video-desktop tw-hidden md:tw-block" loop muted autoplay playsinline disablepictureinpicture>
              <source :src="slide.featured_image.fullUrl" type="video/mp4">
            </video>
          </template>
          <template v-else>
            <picture>
              <source media="(min-width:768px)" :srcset="slide.featured_image?.fullUrl">
              <source media="(max-width:767px)"
                      :srcset="slide.featured_image?.mediaCollection?.['thumbnail-slider'] || slide.featured_image?.fullUrl">
              <img
                :src="slide.featured_image?.mediaCollection?.['thumbnail-slider'] || slide.featured_image?.fullUrl"
                :alt="slide.featured_image?.title || 'Slide'"
                draggable="false"
              >
            </picture>
          </template>
        </a>
      </div>
    </div>

    <div class="slider-shadow-overlay"></div>

    <div class="slide-content-overlay">
      <div
        v-for="(slide, index) in slideshow.dynamicProperties"
        :key="'text-' + index"
        v-show="index === currentIndex"
        class="text-top-left"
      >
        <h2 v-if="slide.DP_titulo || slide.titulo">
          {{ slide.DP_titulo || slide.titulo }}
        </h2>
        <p v-if="slide.description" v-html="slide.description"></p>
        <a v-if="slide.link" :href="slide.link" class="btn-saiba-mais">Saiba mais</a>
      </div>
    </div>

    <div class="fixed-products-sidebar">
      <div v-if="commonProducts.length > 0" class="common-products-list">
        <div
          v-for="product in commonProducts"
          :key="'common-' + product.id"
          class="product-row">

          <div style="pointer-events: auto"
               @click.stop="navigateToProductSlide(product.id)"
               @mousedown.stop
               @mouseup.stop
               class="product-link"
               :class="{ 'is-active': product.id === currentSlideProductId }">
            <span class="product-text">
              {{ product.name }} {{ product.reference }} {{product.label_list}}
            </span>
            <span class="dot-indicator"></span>
          </div>

        </div>
      </div>
    </div>

    <ul v-if="totalSlides > 1" class="slick-dots">
      <li
        v-for="n in totalSlides"
        :key="'dot-' + (n - 1)"
        :class="{ 'slick-active': (n - 1) === currentIndex }"
        @click="goToSlide(n - 1)">
        <div
          class="progress-bar-inner"
          :style="(n - 1) === currentIndex ? { width: `${progress}%`, transition: 'width 100ms linear' } : {}"
        ></div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.rural-slider-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  z-index: 1;
}

/* Track em z-index menor para não prender o contexto dos filhos */
.slider-track {
  z-index: 1;
}

.slider-shadow-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
  background-image:
    linear-gradient(to right, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 300px),
    linear-gradient(to left, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 300px);
  background-position: left center, right center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

/* Novo container de texto absoluto e acima da sombra */
.slide-content-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  pointer-events: none;
}

/* Alinhamento perfeito do texto no canto esquerdo inferior */
.text-top-left {
  position: absolute;
  left: 2rem;
  bottom: 2rem;
  text-align: left;
  pointer-events: auto; /* Reativa cliques para os links funcionarem */
}

.text-top-left h2 {
  color: #ffffff;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.text-top-left p {
  color: #e0e0e0;
  margin-bottom: 1rem;
}

.btn-saiba-mais {
  color: #ee7623;
  text-decoration: underline;
  font-weight: bold;
}

.fixed-products-sidebar {
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  z-index: 20;
  pointer-events: auto;
}

.common-products-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  border-radius: 8px;
  min-width: 200px;
}

.product-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media(max-width: 500.5px) {
    justify-content: flex-end;
  }
}

.product-link {
  display: flex ;
  align-items: center ;
  gap: 10px ;
  text-decoration: none ;
  color: #fff;
  font-size: 30px;
  font-weight: 900;
  transition: all 0.2s ease;
  cursor: pointer ;
  user-select: none;
  @media(max-width: 500.5px) {
    font-size: 20px;
  }
}

.product-link:hover {
  color: #ffffff ;
}

.dot-indicator {
  width: 6px;
  height: 31px;
  background-color: #fff;
  border-radius: 0;
  display: inline-block;
  transition: all 0.2s ease;
}

.product-link:hover .dot-indicator {
  background-color: #ffffff;
}

.product-link.is-active .dot-indicator {
  background-color: #ee7623;
}

.slick-dots {
  z-index: 20;
}
</style>
